import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, Button } from '@components';
import { useKnowledgeBaseProAccess } from '../hooks';
import { KnowledgeBase } from '../types';

interface KnowledgeBasesGridProps {
  knowledgeBases: KnowledgeBase[];
  onOpen: (id: string, tab?: string) => void;
}

export const KnowledgeBasesGrid: React.FC<KnowledgeBasesGridProps> = ({
  knowledgeBases,
  onOpen,
}) => {
  const { t } = useTranslation();
  const { hasProAccess } = useKnowledgeBaseProAccess();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {knowledgeBases.map(kb => (
        <div
          key={kb.id}
          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col gap-3"
        >
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{kb.name}</h3>
            {kb.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                {kb.description}
              </p>
            )}
          </div>
          <KnowledgeBaseContents knowledgeBase={kb} hasProAccess={hasProAccess} />
          <div className="flex flex-wrap items-center gap-2 empty:hidden">
            {hasProAccess && kb.websites.some(website => website.importFailed) && (
              <Badge variant="error" size="sm">
                {t('knowledgeBase.website.importFailedBadge')}
              </Badge>
            )}
            {hasProAccess && kb.pendingWebsitePagesCount > 0 && (
              <Badge variant="info" size="sm">
                {t('knowledgeBase.website.newPages', {
                  count: kb.pendingWebsitePagesCount,
                })}
              </Badge>
            )}
            {hasProAccess && kb.openUnansweredQuestionsCount > 0 && (
              <Badge variant="warning" size="sm">
                {t('knowledgeBase.unanswered.openCount', {
                  count: kb.openUnansweredQuestionsCount,
                })}
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {kb.agents.length === 0 ? (
              t('knowledgeBase.usedByNone')
            ) : (
              <>
                {t('knowledgeBase.usedBy')}{' '}
                {kb.agents.map((agent, index) => (
                  <React.Fragment key={agent.id}>
                    {index > 0 && ', '}
                    <Link
                      to={`/agents/edit/${agent.id}`}
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {agent.name}
                    </Link>
                  </React.Fragment>
                ))}
              </>
            )}
          </p>
          <div className="flex gap-2 mt-auto pt-1">
            <Button size="sm" variant="secondary" className="flex-1" onClick={() => onOpen(kb.id)}>
              {t('knowledgeBase.open')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onOpen(kb.id, 'settings')}>
              {t('knowledgeBase.tabs.settings')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Only what the knowledge base holds; FAQ, company info and websites need the tier
const KnowledgeBaseContents: React.FC<{ knowledgeBase: KnowledgeBase; hasProAccess: boolean }> = ({
  knowledgeBase: kb,
  hasProAccess,
}) => {
  const { t } = useTranslation();
  // In the order of the tabs; websites follow last
  const items = [
    ...(hasProAccess
      ? [
          { icon: 'business', count: kb.companyInfoCount, label: 'knowledgeBase.companyInfoCount' },
          { icon: 'quiz', count: kb.faqEntryCount, label: 'knowledgeBase.faqEntryCount' },
        ]
      : []),
    { icon: 'description', count: kb.documentCount, label: 'knowledgeBase.documentCount' },
  ].filter(item => item.count > 0);
  // Only websites with content: a failed first import shows just the badge below
  const websites = hasProAccess ? kb.websites.filter(website => website.imported) : [];

  if (items.length === 0 && websites.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t('knowledgeBase.empty')}</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
      {items.map(item => (
        <span key={item.icon} className="flex items-center gap-1">
          <span className="material-icons text-base">{item.icon}</span>
          {t(item.label, { count: item.count })}
        </span>
      ))}
      {websites.map(website => (
        <span key={website.rootUrl} className="flex items-center gap-1 min-w-0">
          <span className="material-icons text-base">language</span>
          <span className="truncate">
            {new URL(website.rootUrl).hostname.replace(/^www\./, '')}
          </span>
        </span>
      ))}
    </div>
  );
};
